import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Mail,
  Inbox,
  Send,
  RefreshCw,
  Check,
  Clock,
  User,
  MessageSquare,
  Sparkles,
  CheckCircle,
  Trash2,
  Archive,
  ArchiveRestore,
  FolderOpen,
  CheckSquare,
  Square,
  XSquare,
  AlertTriangle,
  Tag,
  Languages,
  Loader2,
  Wand2,
  Filter,
  SortDesc
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import LouisAvatar from "../components/LouisAvatar";

// Category badge component
const CategoryBadge = ({ category }) => {
  const categoryConfig = {
    client: { label: "Client", class: "bg-blue-100 text-blue-700", icon: User },
    partenaire: { label: "Partenaire", class: "bg-purple-100 text-purple-700", icon: User },
    media: { label: "Média", class: "bg-pink-100 text-pink-700", icon: Mail },
    reclamation: { label: "Réclamation", class: "bg-red-100 text-red-700", icon: AlertTriangle },
    spam: { label: "Spam", class: "bg-gray-100 text-gray-500", icon: Trash2 },
    autre: { label: "Autre", class: "bg-slate-100 text-slate-600", icon: Tag }
  };
  
  if (!category) return null;
  
  const config = categoryConfig[category] || categoryConfig.autre;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.class}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

// Urgency badge component
const UrgencyBadge = ({ urgency }) => {
  const urgencyConfig = {
    haute: { label: "Urgent", class: "bg-red-500 text-white animate-pulse", icon: AlertTriangle },
    moyenne: { label: "Normal", class: "bg-orange-100 text-orange-700", icon: Clock },
    basse: { label: "Basse", class: "bg-green-100 text-green-700", icon: Check }
  };
  
  if (!urgency) return null;
  
  const config = urgencyConfig[urgency] || urgencyConfig.moyenne;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.class}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const statusMap = {
    new: { label: "Nouveau", class: "bg-blue-100 text-blue-700", icon: Inbox },
    pending_response: { label: "Réponse IA prête", class: "bg-yellow-100 text-yellow-700", icon: Sparkles },
    validated: { label: "Validé", class: "bg-green-100 text-green-700", icon: Check },
    sent: { label: "Envoyé", class: "bg-emerald-100 text-emerald-700", icon: Send },
    archived: { label: "Archivé", class: "bg-gray-100 text-gray-500", icon: Archive }
  };
  
  const { label, class: className, icon: Icon } = statusMap[status] || statusMap.new;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${className}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

// Selection toolbar component - moved outside to avoid re-creation on each render
const SelectionToolbar = ({ 
  emailList, 
  selectedSet, 
  setSelectedSet, 
  tabType,
  onBulkArchive,
  onBulkDelete
}) => {
  const count = selectedSet.size;
  const isArchived = tabType === 'archived';
  const isSent = tabType === 'sent';
  const isInbox = tabType === 'inbox';
  
  const selectAll = () => {
    setSelectedSet(new Set(emailList.map(e => e.id)));
  };
  
  const deselectAll = () => {
    setSelectedSet(new Set());
  };
  
  return (
    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg mb-4">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => count === emailList.length ? deselectAll() : selectAll()}
          className="text-sm"
        >
          {count === emailList.length ? (
            <><XSquare className="w-4 h-4 mr-2" /> Tout désélectionner</>
          ) : (
            <><CheckSquare className="w-4 h-4 mr-2" /> Tout sélectionner</>
          )}
        </Button>
        {count > 0 && (
          <span className="text-sm text-muted-foreground">
            {count} email(s) sélectionné(s)
          </span>
        )}
      </div>
      
      {count > 0 && (
        <div className="flex gap-2">
          {isInbox && onBulkArchive && (
            <Button
              variant="outline"
              size="sm"
              onClick={onBulkArchive}
              className="text-orange-600 border-orange-200 hover:bg-orange-50"
            >
              <Archive className="w-4 h-4 mr-2" />
              Archiver ({count})
            </Button>
          )}
          {(isArchived || isSent) && onBulkDelete && (
            <Button
              variant="outline"
              size="sm"
              onClick={onBulkDelete}
              className="text-destructive border-destructive/30 hover:bg-destructive/10"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer ({count})
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default function EmailsPage() {
  const [emails, setEmails] = useState([]);
  const [archivedEmails, setArchivedEmails] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [editedResponse, setEditedResponse] = useState("");
  const [sending, setSending] = useState({});
  const [activeTab, setActiveTab] = useState("inbox");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(null);
  
  // New states for enhanced features
  const [classifying, setClassifying] = useState(false);
  const [generatingResponse, setGeneratingResponse] = useState(false);
  const [aiSuggestedResponse, setAiSuggestedResponse] = useState("");
  const [showAiPreview, setShowAiPreview] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterUrgency, setFilterUrgency] = useState("all");
  const [reminders, setReminders] = useState([]);
  
  // Selection state for each tab
  const [selectedInbox, setSelectedInbox] = useState(new Set());
  const [selectedArchived, setSelectedArchived] = useState(new Set());
  const [selectedSent, setSelectedSent] = useState(new Set());

  useEffect(() => {
    fetchEmails();
    fetchArchivedEmails();
    fetchSentEmails();
    fetchStats();
    fetchReminders();
  }, []);

  const fetchEmails = async () => {
    try {
      const response = await axios.get(`${API}/emails/pending`);
      setEmails(response.data);
      setSelectedInbox(new Set());
    } catch (error) {
      console.error("Error fetching emails:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReminders = async () => {
    try {
      const response = await axios.get(`${API}/emails/enhanced/reminders`);
      setReminders(response.data.reminders || []);
    } catch (error) {
      console.error("Error fetching reminders:", error);
    }
  };

  // Classify a single email
  const classifyEmail = async (emailId) => {
    setClassifying(true);
    try {
      const response = await axios.post(`${API}/emails/enhanced/classify/${emailId}`);
      toast.success(`Classifié: ${response.data.category} (${response.data.urgency})`);
      fetchEmails();
    } catch (error) {
      toast.error("Erreur de classification");
    } finally {
      setClassifying(false);
    }
  };

  // Classify all unclassified emails
  const classifyAllEmails = async () => {
    setClassifying(true);
    try {
      const response = await axios.post(`${API}/emails/enhanced/classify-all-new`);
      toast.success(response.data.message);
      fetchEmails();
    } catch (error) {
      toast.error("Erreur de classification");
    } finally {
      setClassifying(false);
    }
  };

  // Generate AI response suggestion
  const generateAiResponse = async (email) => {
    setGeneratingResponse(true);
    setAiSuggestedResponse("");
    setShowAiPreview(true);
    try {
      const response = await axios.post(`${API}/emails/enhanced/smart-response`, {
        email_id: email.id,
        subject: email.subject,
        body: email.body,
        sender: email.sender,
        category: email.category
      });
      setAiSuggestedResponse(response.data.suggested_response);
    } catch (error) {
      toast.error("Erreur de génération");
      setShowAiPreview(false);
    } finally {
      setGeneratingResponse(false);
    }
  };

  // Use AI suggested response
  const useAiResponse = () => {
    setEditedResponse(aiSuggestedResponse);
    setShowAiPreview(false);
    toast.success("Réponse IA appliquée");
  };

  // Translate email
  const translateEmail = async (emailId, targetLang) => {
    setTranslating(true);
    try {
      const response = await axios.post(`${API}/emails/enhanced/translate/${emailId}?target_lang=${targetLang}`);
      toast.success(`Traduit en ${targetLang.toUpperCase()}`);
      // Update the selected email with translation
      if (selectedEmail && selectedEmail.id === emailId) {
        setSelectedEmail({
          ...selectedEmail,
          translated_body: response.data.translated_body,
          translations: {
            ...selectedEmail.translations,
            [targetLang]: { text: response.data.translated_body }
          }
        });
      }
    } catch (error) {
      toast.error("Erreur de traduction");
    } finally {
      setTranslating(false);
    }
  };

  // Filter emails by category and urgency
  const getFilteredEmails = (emailList) => {
    return emailList.filter(email => {
      const matchCategory = filterCategory === "all" || email.category === filterCategory;
      const matchUrgency = filterUrgency === "all" || email.urgency === filterUrgency;
      return matchCategory && matchUrgency;
    });
  };

  const fetchArchivedEmails = async () => {
    try {
      const response = await axios.get(`${API}/emails/archived`);
      setArchivedEmails(response.data);
      setSelectedArchived(new Set());
    } catch (error) {
      console.error("Error fetching archived emails:", error);
    }
  };

  const fetchSentEmails = async () => {
    try {
      const response = await axios.get(`${API}/emails/sent`);
      setSentEmails(response.data);
      setSelectedSent(new Set());
    } catch (error) {
      console.error("Error fetching sent emails:", error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/emails/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleFetchNewEmails = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/emails/fetch`);
      toast.success(response.data.message);
      fetchEmails();
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors de la récupération des emails");
    } finally {
      setProcessing(false);
    }
  };

  const handleGenerateResponses = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/emails/process`);
      toast.success(response.data.message);
      fetchEmails();
    } catch (error) {
      toast.error("Erreur lors de la génération des réponses");
    } finally {
      setProcessing(false);
    }
  };

  const handleOpenEmail = (email) => {
    setSelectedEmail(email);
    setEditedResponse(email.ai_response || email.validated_response || "");
  };

  const handleValidateAndSend = async () => {
    if (!selectedEmail || !editedResponse.trim()) return;
    
    setSending(prev => ({ ...prev, [selectedEmail.id]: true }));
    
    try {
      await axios.post(`${API}/emails/validate`, {
        email_id: selectedEmail.id,
        response_text: editedResponse
      });
      
      await axios.post(`${API}/emails/${selectedEmail.id}/send`);
      
      toast.success("Email envoyé avec succès !");
      setSelectedEmail(null);
      fetchEmails();
      fetchSentEmails();
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setSending(prev => ({ ...prev, [selectedEmail?.id]: false }));
    }
  };

  const handleValidateOnly = async () => {
    if (!selectedEmail || !editedResponse.trim()) return;
    
    try {
      await axios.post(`${API}/emails/validate`, {
        email_id: selectedEmail.id,
        response_text: editedResponse
      });
      
      toast.success("Réponse validée (non envoyée)");
      setSelectedEmail(null);
      fetchEmails();
    } catch (error) {
      toast.error("Erreur lors de la validation");
    }
  };

  // Archive email
  const handleArchiveEmail = async (emailId, e) => {
    if (e) e.stopPropagation();
    
    try {
      await axios.post(`${API}/emails/${emailId}/archive`);
      toast.success("Email archivé");
      setSelectedEmail(null);
      fetchEmails();
      fetchArchivedEmails();
      fetchSentEmails();
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors de l'archivage");
    }
  };

  // Restore email from archives
  const handleRestoreEmail = async (emailId, e) => {
    if (e) e.stopPropagation();
    
    try {
      await axios.post(`${API}/emails/${emailId}/restore`);
      toast.success("Email restauré");
      fetchEmails();
      fetchArchivedEmails();
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors de la restauration");
    }
  };

  // Delete email permanently
  const handleDeletePermanently = async (emailId) => {
    try {
      await axios.delete(`${API}/emails/${emailId}/permanent`);
      toast.success("Email supprimé définitivement");
      setDeleteConfirm(null);
      fetchArchivedEmails();
      fetchSentEmails();
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Selection handlers
  const toggleSelection = (emailId, selectedSet, setSelectedSet) => {
    const newSet = new Set(selectedSet);
    if (newSet.has(emailId)) {
      newSet.delete(emailId);
    } else {
      newSet.add(emailId);
    }
    setSelectedSet(newSet);
  };

  const selectAll = (emailList, setSelectedSet) => {
    setSelectedSet(new Set(emailList.map(e => e.id)));
  };

  const deselectAll = (setSelectedSet) => {
    setSelectedSet(new Set());
  };

  // Bulk actions
  const handleBulkArchive = async () => {
    const emailIds = Array.from(selectedInbox);
    if (emailIds.length === 0) return;
    
    try {
      const response = await axios.post(`${API}/emails/bulk-archive`, { email_ids: emailIds });
      toast.success(response.data.message);
      fetchEmails();
      fetchArchivedEmails();
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors de l'archivage en masse");
    }
  };

  const handleBulkDelete = async (selectedSet) => {
    const emailIds = Array.from(selectedSet);
    if (emailIds.length === 0) return;
    
    try {
      const response = await axios.post(`${API}/emails/bulk-delete`, { email_ids: emailIds });
      toast.success(response.data.message);
      setBulkDeleteConfirm(null);
      fetchArchivedEmails();
      fetchSentEmails();
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors de la suppression en masse");
    }
  };

  // Render email card with checkbox
  const renderEmailCard = (email, tabType, selectedSet, setSelectedSet) => {
    const isSelected = selectedSet.has(email.id);
    const isArchived = tabType === 'archived';
    const isSent = tabType === 'sent';
    const isInbox = tabType === 'inbox';
    
    // Check if email needs reminder (in reminders list)
    const needsReminder = reminders.some(r => r.email_id === email.id);
    
    return (
      <Card 
        key={email.id} 
        className={`post-preview cursor-pointer hover:shadow-md transition-all ${isSelected ? 'ring-2 ring-primary bg-primary/5' : ''} ${needsReminder ? 'border-l-4 border-l-red-500' : ''}`}
        data-testid={`email-${email.id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            {/* Checkbox */}
            <div 
              className="pt-1"
              onClick={(e) => {
                e.stopPropagation();
                toggleSelection(email.id, selectedSet, setSelectedSet);
              }}
            >
              <Checkbox 
                checked={isSelected}
                onCheckedChange={() => toggleSelection(email.id, selectedSet, setSelectedSet)}
                data-testid={`checkbox-${email.id}`}
              />
            </div>
            
            {/* User icon with urgency indicator */}
            <div 
              className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                email.urgency === 'haute' ? 'bg-red-100' : 
                email.urgency === 'moyenne' ? 'bg-orange-100' : 
                'bg-primary/10'
              }`}
              onClick={() => handleOpenEmail(email)}
            >
              <User className={`w-5 h-5 ${
                email.urgency === 'haute' ? 'text-red-600' : 
                email.urgency === 'moyenne' ? 'text-orange-600' : 
                'text-primary'
              }`} />
            </div>
            
            {/* Email content */}
            <div 
              className="flex-1 min-w-0"
              onClick={() => handleOpenEmail(email)}
            >
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <StatusBadge status={email.status} />
                {email.category && <CategoryBadge category={email.category} />}
                {email.urgency && <UrgencyBadge urgency={email.urgency} />}
                <span className="text-xs text-muted-foreground">
                  {new Date(email.sent_at || email.archived_at || email.received_at).toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
              <p className="font-medium">{email.subject || "Sans sujet"}</p>
              <p className="text-sm text-muted-foreground">{email.from_address || email.sender}</p>
              {email.ai_summary && (
                <p className="text-xs text-primary mt-1 italic">
                  💡 {email.ai_summary}
                </p>
              )}
              <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                {email.body?.substring(0, 150)}...
              </p>
              {isSent && email.validated_response && (
                <div className="mt-2 p-2 bg-emerald-50 rounded text-xs text-emerald-700">
                  <strong>Réponse envoyée:</strong> {email.validated_response.substring(0, 100)}...
                </div>
              )}
              {needsReminder && (
                <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-700 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Relance nécessaire
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex gap-2 flex-wrap justify-end">
              {/* Classify button for unclassified emails */}
              {isInbox && !email.category && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    classifyEmail(email.id);
                  }}
                  disabled={classifying}
                  title="Classifier avec IA"
                  className="text-purple-600 hover:bg-purple-50"
                >
                  {classifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />}
                </Button>
              )}
              {isArchived && (
                <>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRestoreEmail(email.id, e);
                    }}
                    title="Restaurer"
                  >
                    <ArchiveRestore className="w-4 h-4 mr-1" />
                    Restaurer
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-destructive hover:bg-destructive/10"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteConfirm(email);
                    }}
                    title="Supprimer définitivement"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </>
              )}
              {isSent && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="text-orange-600 hover:bg-orange-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleArchiveEmail(email.id, e);
                  }}
                  title="Archiver"
                >
                  <Archive className="w-4 h-4" />
                </Button>
              )}
              {isInbox && (
                <>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenEmail(email);
                    }}
                  >
                    <MessageSquare className="w-4 h-4 mr-1" />
                    Voir
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-orange-600 hover:bg-orange-50"
                    onClick={(e) => handleArchiveEmail(email.id, e)}
                    title="Archiver"
                  >
                    <Archive className="w-4 h-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="ai-thinking">
          <span>Chargement des emails...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-up" data-testid="emails-page">
      {/* Header avec stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card className="stat-card">
          <CardContent className="p-4 text-center">
            <Mail className="w-6 h-6 mx-auto mb-2 text-blue-500" />
            <p className="text-2xl font-bold">{stats.total || 0}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4 text-center">
            <Inbox className="w-6 h-6 mx-auto mb-2 text-yellow-500" />
            <p className="text-2xl font-bold">{stats.new || 0}</p>
            <p className="text-xs text-muted-foreground">Nouveaux</p>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4 text-center">
            <Sparkles className="w-6 h-6 mx-auto mb-2 text-purple-500" />
            <p className="text-2xl font-bold">{stats.pending_response || 0}</p>
            <p className="text-xs text-muted-foreground">À valider</p>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-6 h-6 mx-auto mb-2 text-green-500" />
            <p className="text-2xl font-bold">{stats.validated || 0}</p>
            <p className="text-xs text-muted-foreground">Validés</p>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4 text-center">
            <Send className="w-6 h-6 mx-auto mb-2 text-emerald-500" />
            <p className="text-2xl font-bold">{stats.sent || 0}</p>
            <p className="text-xs text-muted-foreground">Envoyés</p>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4 text-center">
            <Archive className="w-6 h-6 mx-auto mb-2 text-orange-500" />
            <p className="text-2xl font-bold">{stats.archived || 0}</p>
            <p className="text-xs text-muted-foreground">Archivés</p>
          </CardContent>
        </Card>
      </div>

      {/* Toolbar avec filtres et actions IA */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-muted/30 rounded-lg">
        <div className="flex items-center gap-3">
          {/* Filtre par catégorie */}
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[150px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Catégorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes catégories</SelectItem>
              <SelectItem value="client">👤 Client</SelectItem>
              <SelectItem value="partenaire">🤝 Partenaire</SelectItem>
              <SelectItem value="media">📰 Média</SelectItem>
              <SelectItem value="reclamation">⚠️ Réclamation</SelectItem>
              <SelectItem value="spam">🗑️ Spam</SelectItem>
              <SelectItem value="autre">📁 Autre</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Filtre par urgence */}
          <Select value={filterUrgency} onValueChange={setFilterUrgency}>
            <SelectTrigger className="w-[140px]">
              <SortDesc className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Urgence" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes urgences</SelectItem>
              <SelectItem value="haute">🔴 Haute</SelectItem>
              <SelectItem value="moyenne">🟠 Moyenne</SelectItem>
              <SelectItem value="basse">🟢 Basse</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Reminders indicator */}
          {reminders.length > 0 && (
            <Badge variant="destructive" className="animate-pulse">
              <AlertTriangle className="w-3 h-3 mr-1" />
              {reminders.length} relance(s)
            </Badge>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {/* Classify all button */}
          <Button
            variant="outline"
            size="sm"
            onClick={classifyAllEmails}
            disabled={classifying}
            className="text-purple-600 border-purple-200 hover:bg-purple-50"
          >
            {classifying ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Wand2 className="w-4 h-4 mr-2" />
            )}
            Classifier tout
          </Button>
          
          {/* Refresh */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              fetchEmails();
              fetchArchivedEmails();
              fetchSentEmails();
              fetchStats();
              fetchReminders();
            }}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
          <TabsList>
            <TabsTrigger value="inbox" className="flex items-center gap-2" data-testid="tab-inbox">
              <Inbox className="w-4 h-4" />
              Réception
              {emails.length > 0 && (
                <Badge variant="secondary" className="ml-1">{emails.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="sent" className="flex items-center gap-2" data-testid="tab-sent">
              <Send className="w-4 h-4" />
              Envoyés
              {sentEmails.length > 0 && (
                <Badge variant="outline" className="ml-1 bg-emerald-50 text-emerald-700">{sentEmails.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="archives" className="flex items-center gap-2" data-testid="tab-archives">
              <FolderOpen className="w-4 h-4" />
              Archives
              {archivedEmails.length > 0 && (
                <Badge variant="outline" className="ml-1">{archivedEmails.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {activeTab === "inbox" && (
            <div className="flex items-center gap-2">
              <Button
                onClick={handleFetchNewEmails}
                disabled={processing}
                variant="outline"
                data-testid="fetch-emails-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${processing ? 'animate-spin' : ''}`} />
                Récupérer les emails
              </Button>
              <Button
                onClick={handleGenerateResponses}
                disabled={processing}
                className="bg-secondary hover:bg-secondary/90"
                data-testid="generate-responses-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Générer les réponses IA
              </Button>
            </div>
          )}
        </div>

        {/* Boîte de réception */}
        <TabsContent value="inbox">
          {getFilteredEmails(emails).length === 0 ? (
            <Card>
              <CardContent className="py-16">
                <div className="empty-state">
                  <Inbox className="w-16 h-16 text-muted-foreground/50 mb-4" />
                  <h3 className="text-lg font-semibold">
                    {emails.length === 0 ? "Aucun email en attente" : "Aucun email correspond aux filtres"}
                  </h3>
                  <p className="text-muted-foreground mt-2">
                    {emails.length === 0 
                      ? 'Cliquez sur "Récupérer les emails" pour vérifier les nouveaux messages'
                      : "Modifiez vos filtres pour voir plus d'emails"
                    }
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              <SelectionToolbar 
                emailList={getFilteredEmails(emails)} 
                selectedSet={selectedInbox} 
                setSelectedSet={setSelectedInbox}
                tabType="inbox"
                onBulkArchive={handleBulkArchive}
                onBulkDelete={null}
              />
              <div className="space-y-3">
                {getFilteredEmails(emails).map((email) => renderEmailCard(email, 'inbox', selectedInbox, setSelectedInbox))}
              </div>
            </>
          )}
        </TabsContent>

        {/* Envoyés */}
        <TabsContent value="sent">
          {sentEmails.length === 0 ? (
            <Card>
              <CardContent className="py-16">
                <div className="empty-state">
                  <Send className="w-16 h-16 text-muted-foreground/50 mb-4" />
                  <h3 className="text-lg font-semibold">Aucun email envoyé</h3>
                  <p className="text-muted-foreground mt-2">
                    Les réponses envoyées apparaîtront ici
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              <SelectionToolbar 
                emailList={sentEmails} 
                selectedSet={selectedSent} 
                setSelectedSet={setSelectedSent}
                tabType="sent"
                onBulkArchive={null}
                onBulkDelete={() => setBulkDeleteConfirm({ set: selectedSent, type: 'sent' })}
              />
              <div className="space-y-3">
                {sentEmails.map((email) => renderEmailCard(email, 'sent', selectedSent, setSelectedSent))}
              </div>
            </>
          )}
        </TabsContent>

        {/* Archives */}
        <TabsContent value="archives">
          {archivedEmails.length === 0 ? (
            <Card>
              <CardContent className="py-16">
                <div className="empty-state">
                  <FolderOpen className="w-16 h-16 text-muted-foreground/50 mb-4" />
                  <h3 className="text-lg font-semibold">Aucun email archivé</h3>
                  <p className="text-muted-foreground mt-2">
                    Les emails archivés apparaîtront ici
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              <SelectionToolbar 
                emailList={archivedEmails} 
                selectedSet={selectedArchived} 
                setSelectedSet={setSelectedArchived}
                tabType="archived"
                onBulkArchive={null}
                onBulkDelete={() => setBulkDeleteConfirm({ set: selectedArchived, type: 'archived' })}
              />
              <div className="space-y-3">
                {archivedEmails.map((email) => renderEmailCard(email, 'archived', selectedArchived, setSelectedArchived))}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialog de réponse / consultation */}
      <Dialog open={!!selectedEmail} onOpenChange={() => setSelectedEmail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              {selectedEmail?.status === 'sent' ? 'Email envoyé' : 
               selectedEmail?.status === 'archived' ? 'Email archivé' : 
               'Répondre à l\'email'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedEmail && (
            <div className="space-y-4 py-4">
              {/* Email original */}
              <div className="p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">{selectedEmail.from_address}</span>
                  <StatusBadge status={selectedEmail.status} />
                </div>
                <p className="font-medium mb-2">{selectedEmail.subject}</p>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {selectedEmail.body}
                </p>
              </div>

              {/* Réponse - Mode lecture pour envoyés, édition pour les autres */}
              {selectedEmail.status === 'sent' ? (
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="flex items-center gap-2 mb-3">
                    <LouisAvatar size={24} />
                    <span className="font-medium text-emerald-800">Réponse envoyée</span>
                    <Badge className="ml-auto bg-emerald-100 text-emerald-700">
                      <Send className="w-3 h-3 mr-1" />
                      Envoyé le {new Date(selectedEmail.sent_at).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Badge>
                  </div>
                  <p className="text-sm text-emerald-900 whitespace-pre-wrap">
                    {selectedEmail.validated_response}
                  </p>
                </div>
              ) : selectedEmail.status === 'archived' ? (
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Archive className="w-5 h-5 text-gray-500" />
                    <span className="font-medium text-gray-700">Email archivé</span>
                    {selectedEmail.ai_response && (
                      <Badge variant="outline" className="ml-auto">
                        <Sparkles className="w-3 h-3 mr-1" />
                        Réponse IA disponible
                      </Badge>
                    )}
                  </div>
                  {selectedEmail.ai_response && (
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">
                      {selectedEmail.ai_response}
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <LouisAvatar size={24} />
                    <span className="font-medium">Réponse de Louis</span>
                    {selectedEmail.ai_response && (
                      <Badge variant="outline" className="ml-auto">
                        <Sparkles className="w-3 h-3 mr-1" />
                        Générée par IA
                      </Badge>
                    )}
                  </div>
                  
                  {/* AI Response Generation Button */}
                  <div className="flex gap-2 mb-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => generateAiResponse(selectedEmail)}
                      disabled={generatingResponse}
                      className="text-purple-600 border-purple-200 hover:bg-purple-50"
                    >
                      {generatingResponse ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Wand2 className="w-4 h-4 mr-2" />
                      )}
                      Générer réponse IA
                    </Button>
                    
                    {/* Translation dropdown */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={translating}
                          className="text-blue-600 border-blue-200 hover:bg-blue-50"
                        >
                          {translating ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Languages className="w-4 h-4 mr-2" />
                          )}
                          Traduire
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem onClick={() => translateEmail(selectedEmail.id, 'fr')}>
                          🇫🇷 Français
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => translateEmail(selectedEmail.id, 'en')}>
                          🇬🇧 English
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => translateEmail(selectedEmail.id, 'es')}>
                          🇪🇸 Español
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  
                  {/* AI Preview Modal */}
                  {showAiPreview && (
                    <div className="mb-3 p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-purple-800 flex items-center gap-2">
                          <Sparkles className="w-4 h-4" />
                          Suggestion IA
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowAiPreview(false)}
                          className="h-6 w-6 p-0"
                        >
                          ×
                        </Button>
                      </div>
                      {generatingResponse ? (
                        <div className="flex items-center gap-2 text-sm text-purple-600">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Génération en cours...
                        </div>
                      ) : (
                        <>
                          <p className="text-sm text-purple-900 whitespace-pre-wrap max-h-48 overflow-y-auto">
                            {aiSuggestedResponse}
                          </p>
                          <div className="flex justify-end mt-3">
                            <Button
                              size="sm"
                              onClick={useAiResponse}
                              className="bg-purple-600 hover:bg-purple-700"
                            >
                              <Check className="w-4 h-4 mr-2" />
                              Utiliser cette réponse
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  
                  {/* Translated content */}
                  {selectedEmail.translated_body && (
                    <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <span className="text-xs font-medium text-blue-800 flex items-center gap-1 mb-2">
                        <Languages className="w-3 h-3" />
                        Traduction
                      </span>
                      <p className="text-sm text-blue-900 whitespace-pre-wrap">
                        {selectedEmail.translated_body}
                      </p>
                    </div>
                  )}
                  
                  <Textarea
                    value={editedResponse}
                    onChange={(e) => setEditedResponse(e.target.value)}
                    rows={12}
                    className="w-full"
                    placeholder="La réponse IA apparaîtra ici..."
                    data-testid="email-response-textarea"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Vous pouvez modifier la réponse avant de l&apos;envoyer
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {/* Actions selon le statut */}
            {selectedEmail?.status === 'sent' ? (
              <>
                <Button 
                  variant="ghost"
                  className="text-orange-600 hover:bg-orange-50 mr-auto"
                  onClick={() => handleArchiveEmail(selectedEmail?.id)}
                >
                  <Archive className="w-4 h-4 mr-2" />
                  Archiver
                </Button>
                <Button variant="outline" onClick={() => setSelectedEmail(null)}>
                  Fermer
                </Button>
              </>
            ) : selectedEmail?.status === 'archived' ? (
              <>
                <Button 
                  variant="ghost"
                  className="text-blue-600 hover:bg-blue-50 mr-auto"
                  onClick={() => {
                    handleRestoreEmail(selectedEmail?.id);
                    setSelectedEmail(null);
                  }}
                >
                  <ArchiveRestore className="w-4 h-4 mr-2" />
                  Restaurer
                </Button>
                <Button variant="outline" onClick={() => setSelectedEmail(null)}>
                  Fermer
                </Button>
              </>
            ) : (
              <>
                <div className="flex gap-2 mr-auto">
                  <Button 
                    variant="ghost"
                    className="text-orange-600 hover:bg-orange-50"
                    onClick={() => handleArchiveEmail(selectedEmail?.id)}
                  >
                    <Archive className="w-4 h-4 mr-2" />
                    Archiver
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setSelectedEmail(null)}>
                    Annuler
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={handleValidateOnly}
                    disabled={!editedResponse.trim()}
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Valider sans envoyer
                  </Button>
                  <Button 
                    className="btn-primary"
                    onClick={handleValidateAndSend}
                    disabled={!editedResponse.trim() || sending[selectedEmail?.id]}
                    data-testid="send-email-btn"
                  >
                    {sending[selectedEmail?.id] ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Envoi...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Valider et envoyer
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmation de suppression unique */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              Supprimer définitivement ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. L&apos;email sera définitivement supprimé.
              <div className="mt-3 p-3 bg-muted rounded-lg">
                <p className="font-medium">{deleteConfirm?.subject || "Sans sujet"}</p>
                <p className="text-sm text-muted-foreground">{deleteConfirm?.from_address}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => handleDeletePermanently(deleteConfirm?.id)}
            >
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de confirmation de suppression en masse */}
      <AlertDialog open={!!bulkDeleteConfirm} onOpenChange={() => setBulkDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              Supprimer {bulkDeleteConfirm?.set?.size} email(s) définitivement ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Les {bulkDeleteConfirm?.set?.size} emails sélectionnés seront définitivement supprimés et ne pourront pas être récupérés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => handleBulkDelete(bulkDeleteConfirm?.set)}
            >
              Supprimer {bulkDeleteConfirm?.set?.size} email(s)
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
